import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Box, Center, Text } from '@mantine/core';
import { IconArrowsDiff } from '@tabler/icons-react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAllProducts, getProductById, isProductLockedResponse } from '@/Api';
import { getJson } from '@/services/httpClient';
import { userPath } from '@/ApiPaths';
import { pageWrapper } from '@/Styles';
import { PageHeader } from '@/components/PageHeader';
import { CompareProductPanel } from '@/components/CompareProducts/CompareProductPanel';
import { CompareSlotSearch } from '@/components/CompareProducts/CompareSlotSearch';
import { CompareMetadataTable } from '@/components/CompareProducts/CompareMetadataTable';
import {
  MAX_COMPARE,
  toCompareSelectionItem,
  useCompareStore,
  type CompareSelectionItem,
} from '@/store/compareStore';
import { useStore, type UserProfile } from '@/store/Store';
import type { IProduct } from '@/types/Product.model';
import type { CompareProductSource } from '@/utils/compareMetadata';
import { colorWithAlpha } from '@/utils/colorWithAlpha';
import { DEFAULT_QR_CODE_COLOR } from '@/theme';
import classes from './CompareProducts.module.css';

function parseIdsParam(raw: string | null): string[] {
  if (!raw) return [];
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const part of raw.split(',')) {
    const id = part.trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
    if (ids.length >= MAX_COMPARE) break;
  }
  return ids;
}

function selectionFromIds(
  ids: string[],
  catalogById: Map<string, IProduct>
): CompareSelectionItem[] {
  return ids.map((id) => {
    const found = catalogById.get(id);
    return (
      toCompareSelectionItem(found ?? { _id: id, productName: id }) ?? {
        _id: id,
        productName: id,
      }
    );
  });
}

export function CompareProducts() {
  const { t } = useTranslation();
  const accentColor = useStore((s) => s.userProfile.qrCodeColor) || DEFAULT_QR_CODE_COLOR;
  const [searchParams, setSearchParams] = useSearchParams();
  const didInitFromUrl = useRef(false);
  const [urlSyncEnabled, setUrlSyncEnabled] = useState(false);

  const items = useCompareStore((s) => s.items);
  const add = useCompareStore((s) => s.add);
  const remove = useCompareStore((s) => s.remove);
  const setItems = useCompareStore((s) => s.setItems);

  const [catalog, setCatalog] = useState<IProduct[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState(false);
  const [catalogAttempt, setCatalogAttempt] = useState(0);
  const [detailById, setDetailById] = useState<Record<string, CompareProductSource>>({});
  const [detailsLoading, setDetailsLoading] = useState(false);

  const selectedIds = useMemo(() => items.map((i) => i._id), [items]);

  const catalogById = useMemo(() => {
    const map = new Map<string, IProduct>();
    for (const product of catalog) map.set(product._id, product);
    return map;
  }, [catalog]);

  // Deep-link / tray: apply ?ids= once, then enable URL sync on the next render
  useEffect(() => {
    if (didInitFromUrl.current) return;
    didInitFromUrl.current = true;
    const urlIds = parseIdsParam(searchParams.get('ids'));
    if (urlIds.length > 0) {
      setItems(selectionFromIds(urlIds, catalogById));
    }
    setUrlSyncEnabled(true);
  }, [searchParams, catalogById, setItems]);

  // Keep URL in sync with store (store is source of truth after init)
  useEffect(() => {
    if (!urlSyncEnabled) return;
    const next = selectedIds.join(',');
    const current = parseIdsParam(searchParams.get('ids')).join(',');
    if (next === current) return;
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (!next) params.delete('ids');
        else params.set('ids', next);
        return params;
      },
      { replace: true }
    );
  }, [urlSyncEnabled, selectedIds, searchParams, setSearchParams]);

  const retryCatalog = useCallback(() => {
    setCatalogAttempt((attempt) => attempt + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setCatalogLoading(true);
    setCatalogError(false);
    getAllProducts()
      .then((products) => {
        if (!cancelled) setCatalog(products.filter((p) => Boolean(p._id)));
      })
      .catch(() => {
        if (!cancelled) {
          setCatalog([]);
          setCatalogError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [catalogAttempt]);

  // Enrich tray snapshots when catalog loads
  useEffect(() => {
    if (catalog.length === 0 || items.length === 0) return;
    let changed = false;
    const next = items.map((item) => {
      const found = catalogById.get(item._id);
      if (!found) return item;
      const enriched = toCompareSelectionItem(found);
      if (!enriched) return item;
      if (
        enriched.productName !== item.productName ||
        enriched.productImage !== item.productImage ||
        enriched.imageVersion !== item.imageVersion ||
        enriched.articleNumber !== item.articleNumber
      ) {
        changed = true;
        return enriched;
      }
      return item;
    });
    if (changed) setItems(next);
  }, [catalog, catalogById, items, setItems]);

  // Load full products (+ owner) for metadata table
  useEffect(() => {
    if (selectedIds.length === 0) {
      setDetailById({});
      return;
    }
    let cancelled = false;
    setDetailsLoading(true);
    Promise.all(
      selectedIds.map(async (id) => {
        try {
          const res = await getProductById(id);
          if (isProductLockedResponse(res)) return null;
          const product = res as IProduct;
          const ownerId =
            typeof product.owningUser_id === 'string' ? product.owningUser_id.trim() : '';
          let companyName: string | undefined;
          let ownerUsername: string | undefined;
          if (ownerId) {
            try {
              const owner = await getJson<Partial<UserProfile>>(userPath + ownerId);
              companyName = owner.companyName?.trim() || undefined;
              ownerUsername = owner.username?.trim() || undefined;
            } catch {
              // Owner profile is optional for compare rows
            }
          }
          const enriched: CompareProductSource = {
            ...product,
            companyName,
            ownerUsername,
          };
          return enriched;
        } catch {
          return null;
        }
      })
    )
      .then((results) => {
        if (cancelled) return;
        const map: Record<string, CompareProductSource> = {};
        for (const product of results) {
          if (product?._id) map[product._id] = product;
        }
        setDetailById(map);
      })
      .finally(() => {
        if (!cancelled) setDetailsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedIds]);

  const metadataProducts = useMemo(
    () => selectedIds.map((id) => detailById[id]).filter(Boolean) as CompareProductSource[],
    [selectedIds, detailById]
  );

  const handleAdd = useCallback(
    (item: CompareSelectionItem) => {
      add(item);
    },
    [add]
  );

  const emptySlotCount = Math.max(0, MAX_COMPARE - items.length);
  const slots: Array<
    { type: 'product'; item: CompareSelectionItem } | { type: 'empty'; key: string }
  > = [
    ...items.map((item) => ({ type: 'product' as const, item })),
    ...Array.from({ length: emptySlotCount }, (_, index) => ({
      type: 'empty' as const,
      key: `empty-${index}`,
    })),
  ];

  return (
    <Center style={{ ...pageWrapper, paddingBottom: 40 }}>
      <Box
        style={{
          width: '80%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 'calc(100vh - 120px)',
        }}
      >
        <Box
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: 'var(--mantine-color-body)',
            paddingTop: 20,
            paddingBottom: 10,
            marginBottom: 20,
          }}
        >
          <PageHeader
            icon={<IconArrowsDiff size={22} stroke={1.75} />}
            title={t('marketplace.compare.title')}
            subtitle={t('marketplace.compare.description')}
            accentColor={accentColor}
            mb={20}
            actions={
              <Badge size="lg" variant="light" color={accentColor}>
                {t('marketplace.compare.selectedCount', {
                  count: items.length,
                  max: MAX_COMPARE,
                })}
              </Badge>
            }
          />
        </Box>

        {items.length === 0 ? (
          <Box
            className={classes.emptyBanner}
            style={{
              borderColor: colorWithAlpha(accentColor, 0.28),
              background: colorWithAlpha(accentColor, 0.08),
            }}
            mb={16}
          >
            <Text fw={600}>{t('marketplace.compare.emptySlots')}</Text>
          </Box>
        ) : null}

        <Box className={classes.compareGrid} mb={24}>
          {slots.map((slot) =>
            slot.type === 'product' ? (
              <CompareProductPanel
                key={slot.item._id}
                productId={slot.item._id}
                fallback={catalogById.get(slot.item._id) ?? null}
                accentColor={accentColor}
                onRemove={() => remove(slot.item._id)}
              />
            ) : (
              <CompareSlotSearch
                key={slot.key}
                accentColor={accentColor}
                selectedIds={selectedIds}
                onAdd={handleAdd}
                catalog={catalog}
                catalogLoading={catalogLoading}
                catalogError={catalogError}
                onRetryCatalog={retryCatalog}
              />
            )
          )}
        </Box>

        <CompareMetadataTable
          products={metadataProducts}
          accentColor={accentColor}
          loading={detailsLoading}
        />
      </Box>
    </Center>
  );
}
